import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Spin, Empty, Button } from 'antd'
import AMapLoader from '@amap/amap-jsapi-loader'
import CONFIG from '../../../../constants/config'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { fetchPhotoMapAgg } from '../../../../actions/aiPhotoMap'
import { buildFetchPhotosParamsWithOptions } from '../../lib/photo'
import { AppDispatch, RootState } from '../../../../app/store'
import './index.scss'

/** 照片地图节点(区县或 POI)。坐标后端已转 GCJ02,高德可直接落点。 */
interface PhotoMapNode {
  name: string
  photoCnt: number
  level: string
  lat: number
  lng: number
  coverUrl: string
  child?: PhotoMapNode[]
}

/** 照片明细点(level=point,排行气球用)。坐标已转 GCJ02。 */
interface PhotoMapPoint {
  lat: number
  lng: number
  coverUrl: string
  userID: string
  photoTime: number
  poi?: string // 该照片的 POI 地名(后端带回时用作地点名;没有则前端回退"地点N")
}

/** /web/address/v1/photo/map_agg 返回的聚合结果 */
interface PhotoMapAggResult {
  list: PhotoMapNode[]
  points?: PhotoMapPoint[]
  totalWithGeo: number
  totalAll: number
}

/**
 * 排行气球模式入参:排行榜每项(任意维度:人/天/部门/城市…)。
 * - userID:该项的唯一 key(实为「名次串」,非真实用户ID;气球按它分桶/上色/下钻)。
 *   保留 userID 这个字段名是为了复用既有按 key 分桶的全部逻辑。
 * - mapFilter:该项自己的 map_agg 过滤(行级范围 = 卡片口径 + 本项维度差异),逐行各拉一次点。
 */
interface RankItem {
  rank: number
  name: string
  userID: string
  mapFilter?: Record<string, any>
}

interface PhotoMapDrawerProps {
  visible: boolean
  onClose: () => void
  /** 可选:直接指定要映射的筛选(map_agg 接口形状)。不传则用当前主页筛选。 */
  mapFilter?: Record<string, any>
  /**
   * 可选:传入排行榜条目(成员维度,带 userID)→ 进入「排行气球」模式:
   * 把这些成员的带位置照片打到地图上,按名次着色,点一个气球高亮同一人。
   * 不传 / 为空 → 走默认的「区县→POI 地点聚合」模式。
   */
  rankItems?: RankItem[]
}

type MapMode = 'clean' | 'normal' | 'satellite'

const MAP_MODES: Array<{ key: MapMode; label: string }> = [
  { key: 'clean', label: '纯净地图' },
  { key: 'normal', label: '普通地图' },
  { key: 'satellite', label: '卫星地图' }
]

/** 名次配色(1→10):冷色/蓝色系,白底地图上不刺眼(张璇:红的太耀眼,改蓝) */
const RANK_COLORS = [
  '#0050b3',
  '#096dd9',
  '#1890ff',
  '#2f54eb',
  '#1d39c4',
  '#10239e',
  '#13c2c2',
  '#08979c',
  '#0e7490',
  '#531dab'
]

const EMPTY_RESULT: PhotoMapAggResult = {
  list: [],
  totalWithGeo: 0,
  totalAll: 0
}

const POINT_LIMIT = 1000
/** 排行气球「逐行各拉」模式下,单行(单个排行项)拉点上限,控制总量(行数 × 此值) */
const PER_ROW_POINT_LIMIT = 500

/**
 * AI问照片「照片地图」浮窗(居中弹窗),两种形态:
 *
 * 1) 默认「地点聚合」:把当前筛选的照片按 区县→POI 两级聚合,右侧地点排行,点击下钻。
 * 2)「排行气球」(传 rankItems 时):把排行榜成员的带位置照片打到地图上,按名次着色,
 *    气球内容 = 名次 + 姓名 + 照片,点一个气球 → 高亮同一人的全部气球(刘乾隆 需求)。
 *
 * 数据始终与卡片同源(mapFilter 指定那张卡的范围;不传则用当前主页筛选)。
 */
const PhotoMapDrawer: React.FC<PhotoMapDrawerProps> = ({
  visible,
  onClose,
  mapFilter,
  rankItems
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const groupID = useSelector((state: RootState) => state.group.groupId)
  const queryFilter = useSelector(selectSearchFilter)

  const containerRef = useRef<HTMLDivElement>(null)
  const aMapRef = useRef<any>(null)
  const mapRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const satelliteRef = useRef<any>(null)
  const roadNetRef = useRef<any>(null)
  // 排行气球:userID → 它的全部 marker(侧栏点名定位 / 浮顶层用)
  const markersByUserRef = useRef<Record<string, any[]>>({})
  // 地点聚合:POI 名 → marker(侧栏点地点排行时把对应 POI 浮顶)
  const poiMarkerByNameRef = useRef<Record<string, any>>({})
  // 排行气球:坐标 key → 气球 marker(侧栏点某成员的某地点时,高亮对应气球)
  const clusterMarkerByKeyRef = useRef<Record<string, any>>({})
  const activeUserRef = useRef<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PhotoMapAggResult | null>(null)
  const [activeDistrict, setActiveDistrict] = useState<PhotoMapNode | null>(
    null
  )
  const [mapMode, setMapMode] = useState<MapMode>('normal')
  const [activeUserID, setActiveUserID] = useState<string | null>(null)
  // 点某成员后,右侧面板下钻成「这个人的各地点」(解决多地点气球重叠点不到的问题)
  const [memberDrill, setMemberDrill] = useState<{
    userID: string
    rank: number
    name: string
  } | null>(null)
  // 照片轮播浮层:某点位/成员的照片 + 当前第几张(气球点 = 成员某点位的照片;POI 点 = 该地点的照片)
  const [viewer, setViewer] = useState<{
    list: PhotoMapPoint[]
    idx: number
    title: string
  } | null>(null)
  // 当前生效的 filter(map_agg 接口形状),POI 点击拉该点位照片时复用
  const currentFilterRef = useRef<any>(null)

  // 是否「排行气球」模式
  const isBalloonMode = !!(rankItems && rankItems.length > 0)
  // userID → { rank, name, color }
  const rankMap = useMemo(() => {
    const m = new Map<string, { rank: number; name: string; color: string }>()
    ;(rankItems || []).forEach((it) => {
      if (it.userID) {
        m.set(it.userID, {
          rank: it.rank,
          name: it.name,
          color: RANK_COLORS[(it.rank - 1) % RANK_COLORS.length]
        })
      }
    })
    return m
  }, [rankItems])
  const rankMapRef = useRef(rankMap)
  rankMapRef.current = rankMap

  /** 清掉地图上现有的所有 marker */
  const clearMarkers = useCallback(() => {
    if (mapRef.current && markersRef.current.length > 0) {
      mapRef.current.remove(markersRef.current)
    }
    markersRef.current = []
    markersByUserRef.current = {}
    poiMarkerByNameRef.current = {}
    clusterMarkerByKeyRef.current = {}
  }, [])

  /** 把当前 markers 自适应到视野内 */
  const fitMarkers = useCallback((markers?: any[]) => {
    const list = markers && markers.length > 0 ? markers : markersRef.current
    if (mapRef.current && list.length > 0) {
      mapRef.current.setFitView(list, false, [60, 60, 60, 60])
    }
  }, [])

  // ===== 地点聚合模式 marker =====
  const districtMarkerContent = useCallback((node: PhotoMapNode) => {
    return `<div class="ai-photo-map-pin ai-photo-map-pin--district"><span class="name">${
      node.name || '未知'
    }</span><span class="cnt">${node.photoCnt}</span></div>`
  }, [])

  const poiMarkerContent = useCallback((node: PhotoMapNode) => {
    const cover = node.coverUrl
      ? `<span class="cover" style="background-image:url('${node.coverUrl}')"></span>`
      : ''
    return `<div class="ai-photo-map-pin ai-photo-map-pin--poi" data-poi="${
      node.name || ''
    }">${cover}<span class="name">${
      node.name || '未知地点'
    }</span><span class="cnt">${node.photoCnt}</span></div>`
  }, [])

  /** 点 POI → 拉该地点的全部照片(point 模式 + poiName)→ 开轮播浮层(可切换、第X/共N)。
   *  注:只按 poiName 过滤,不传 districtName —— district.raw 词条过滤会让 ES all shards failed。 */
  const openPoiPhotos = useCallback(
    async (poi: PhotoMapNode) => {
      if (!groupID || !currentFilterRef.current) {
        return
      }
      // 先用聚合里的单封面占位开浮层(点击即时反馈),拉到明细再替换
      setViewer({
        list: poi.coverUrl
          ? [
              {
                lat: poi.lat,
                lng: poi.lng,
                coverUrl: poi.coverUrl,
                userID: '',
                photoTime: 0
              }
            ]
          : [],
        idx: 0,
        title: poi.name
      })
      try {
        const res: any = await dispatch(
          fetchPhotoMapAgg({
            groupID,
            filter: currentFilterRef.current,
            level: 'point',
            poiName: poi.name,
            pointLimit: POINT_LIMIT
          })
        )
        const data: PhotoMapAggResult =
          (res && (res.payload || res)) || EMPTY_RESULT
        const pts = data.points || []
        if (pts.length > 0) {
          setViewer({ list: pts, idx: 0, title: poi.name })
        }
      } catch (e) {
        // 拉失败则保留占位单封面
      }
    },
    [dispatch, groupID]
  )

  const plotPois = useCallback(
    (district: PhotoMapNode) => {
      clearMarkers()
      const AMap = aMapRef.current
      if (!AMap || !mapRef.current) {
        return
      }
      const pois = (district.child || []).filter((p) => p.lat || p.lng)
      pois.forEach((poi) => {
        const marker = new AMap.Marker({
          position: new AMap.LngLat(poi.lng, poi.lat),
          offset: new AMap.Pixel(0, 0),
          zIndex: 100,
          content: poiMarkerContent(poi)
        })
        // 点 POI → 它浮到顶层、其余 POI 压低 + 透明,放大到该点 + 弹照片明细
        marker.on('click', () => {
          markersRef.current.forEach((mk) => {
            if (mk.setzIndex) {
              mk.setzIndex(mk === marker ? 300 : 100)
            }
          })
          const root = containerRef.current
          if (root) {
            root.querySelectorAll('.ai-photo-map-pin--poi').forEach((n) => {
              const same = n.getAttribute('data-poi') === poi.name
              n.classList.toggle('is-active', same)
              n.classList.toggle('is-dim', !same)
            })
          }
          if (mapRef.current) {
            mapRef.current.setZoomAndCenter(16, [poi.lng, poi.lat])
          }
          openPoiPhotos(poi)
        })
        markersRef.current.push(marker)
        poiMarkerByNameRef.current[poi.name] = marker
      })
      mapRef.current.add(markersRef.current)
      fitMarkers()
    },
    [clearMarkers, fitMarkers, poiMarkerContent, openPoiPhotos]
  )

  const plotDistricts = useCallback(
    (nodes: PhotoMapNode[]) => {
      clearMarkers()
      const AMap = aMapRef.current
      if (!AMap || !mapRef.current) {
        return
      }
      const plotted: Array<{ marker: any; cnt: number }> = []
      nodes.forEach((district) => {
        const geoChild = (district.child || []).find((c) => c.lat || c.lng)
        const lat = district.lat || (geoChild ? geoChild.lat : 0)
        const lng = district.lng || (geoChild ? geoChild.lng : 0)
        if (!lat && !lng) {
          return
        }
        const marker = new AMap.Marker({
          position: new AMap.LngLat(lng, lat),
          offset: new AMap.Pixel(0, 0),
          content: districtMarkerContent(district)
        })
        marker.on('click', () => {
          if ((district.child || []).length > 0) {
            setActiveDistrict(district)
            plotPois(district)
          }
        })
        markersRef.current.push(marker)
        plotted.push({ marker, cnt: district.photoCnt || 0 })
      })
      mapRef.current.add(markersRef.current)
      // 初始视野只框「主要区域」(照片数 ≥ 最高区的 5%),忽略零星离群点(如个别国外地点);
      // 否则 fitView 为同时显示国内 + 国外,会把视野缩到大西洋/非洲(张璇 2026-06 反馈)。
      const maxCnt = plotted.reduce((m, p) => Math.max(m, p.cnt), 0)
      const mainMarkers = plotted
        .filter((p) => p.cnt >= maxCnt * 0.05)
        .map((p) => p.marker)
      fitMarkers(mainMarkers.length > 0 ? mainMarkers : markersRef.current)
    },
    [clearMarkers, fitMarkers, districtMarkerContent, plotPois]
  )

  const backToDistricts = useCallback(() => {
    setActiveDistrict(null)
    plotDistricts((result || EMPTY_RESULT).list)
  }, [plotDistricts, result])

  // ===== 排行气球模式 =====
  /** 高亮某人的全部气球(其余淡化);uid=null 取消高亮。用 DOM 切换 class,不重绘 marker。 */
  const highlightMember = useCallback((uid: string | null) => {
    activeUserRef.current = uid
    const root = containerRef.current
    if (root) {
      root.querySelectorAll('.ai-photo-balloon').forEach((n) => {
        const same = n.getAttribute('data-uid') === uid
        n.classList.toggle('is-active', !!uid && same)
        n.classList.toggle('is-dim', !!uid && !same)
        n.classList.remove('is-selected') // 换成员/重置时清掉旧的地点选中态
      })
    }
    // 选中成员的气球浮到最顶层(其余压低),刘乾隆:点右侧把对应气球浮顶
    Object.keys(markersByUserRef.current).forEach((u) => {
      const z = uid && u === uid ? 300 : 100
      markersByUserRef.current[u].forEach(
        (mk) => mk.setzIndex && mk.setzIndex(z)
      )
    })
  }, [])

  /** 点右侧某地点 → 给对应地点的气球加选中样式 + 浮到最顶(指定是哪个气球) */
  const selectCluster = useCallback((key: string) => {
    const root = containerRef.current
    if (root) {
      root.querySelectorAll('.ai-photo-balloon').forEach((n) => {
        n.classList.toggle('is-selected', n.getAttribute('data-key') === key)
      })
    }
    // 先把所有气球压回基准层(选中成员 300、其余 100),再把目标地点抬到 400,
    // 保证同一时刻只有一个气球浮顶(否则连点多个地点都停在 400,重叠时旧的还盖在上面)
    const activeUid = activeUserRef.current
    Object.keys(markersByUserRef.current).forEach((u) => {
      const z = activeUid && u === activeUid ? 300 : 100
      markersByUserRef.current[u].forEach(
        (mk) => mk.setzIndex && mk.setzIndex(z)
      )
    })
    const mk = clusterMarkerByKeyRef.current[key]
    if (mk && mk.setzIndex) {
      mk.setzIndex(400)
    }
  }, [])

  // 同一人在同一地点(有 poi 按 POI 名、否则按坐标)的多张照片聚成一个气球(带张数);点击看该地点全部照片
  interface BalloonCluster {
    key: string
    userID: string
    lat: number
    lng: number
    name: string
    info: { rank: number; name: string; color: string }
    photos: PhotoMapPoint[]
  }

  const balloonContent = useCallback((c: BalloonCluster) => {
    const cnt = c.photos.length
    const first = c.photos[0]
    const badge = cnt > 1 ? `<span class="b-cnt">${cnt}张</span>` : ''
    const cover =
      first && first.coverUrl
        ? `<span class="b-cover" style="background-image:url('${first.coverUrl}')"></span>`
        : ''
    return `<div class="ai-photo-balloon" data-uid="${c.userID}" data-key="${c.key}" style="--c:${c.info.color}"><span class="b-rank">${c.info.rank}</span><span class="b-name">${c.info.name}</span>${badge}${cover}</div>`
  }, [])

  /** 画排行气球:有 poi 按 (成员, POI 名) 聚合、否则按坐标,一个地点一个气球(带张数),点击看该地点全部照片 */
  const plotBalloons = useCallback(
    (points: PhotoMapPoint[]) => {
      clearMarkers()
      const AMap = aMapRef.current
      if (!AMap || !mapRef.current) {
        return
      }
      const groups = new Map<string, BalloonCluster>()
      points.forEach((pt) => {
        const info = rankMapRef.current.get(pt.userID)
        if (!info || (!pt.lat && !pt.lng)) {
          return
        }
        const place = pt.poi || `${pt.lat.toFixed(6)}|${pt.lng.toFixed(6)}`
        const key = `${pt.userID}|${place}`
        let g = groups.get(key)
        if (!g) {
          g = {
            key,
            userID: pt.userID,
            lat: pt.lat,
            lng: pt.lng,
            name: pt.poi || '',
            info,
            photos: []
          }
          groups.set(key, g)
        }
        g.photos.push(pt)
      })
      groups.forEach((g) => {
        const marker = new AMap.Marker({
          position: new AMap.LngLat(g.lng, g.lat),
          offset: new AMap.Pixel(0, 0),
          zIndex: 100,
          content: balloonContent(g)
        })
        // 点气球:高亮同一人 + 浮顶层 + 打开「这个点位」的照片轮播(单点位切换)
        marker.on('click', () => {
          setActiveUserID(g.userID)
          highlightMember(g.userID)
          setMemberDrill({
            userID: g.userID,
            rank: g.info.rank,
            name: g.info.name
          })
          setViewer({
            list: g.photos,
            idx: 0,
            title: `#${g.info.rank} ${g.info.name}`
          })
        })
        markersRef.current.push(marker)
        if (!markersByUserRef.current[g.userID]) {
          markersByUserRef.current[g.userID] = []
        }
        markersByUserRef.current[g.userID].push(marker)
        clusterMarkerByKeyRef.current[g.key] = marker
      })
      mapRef.current.add(markersRef.current)
      fitMarkers()
    },
    [clearMarkers, fitMarkers, balloonContent, highlightMember]
  )

  /** 侧栏点某成员 → 高亮 ta 的全部气球 + 定位(总是激活,不 toggle,配合面板下钻) */
  const focusMember = useCallback(
    (uid: string) => {
      setActiveUserID(uid)
      highlightMember(uid)
      if (markersByUserRef.current[uid]) {
        fitMarkers(markersByUserRef.current[uid])
      }
    },
    [highlightMember, fitMarkers]
  )

  // ===== 底图模式 =====
  const applyMapMode = useCallback((mode: MapMode) => {
    const AMap = aMapRef.current
    const map = mapRef.current
    if (!AMap || !map) {
      return
    }
    if (mode === 'satellite') {
      if (!satelliteRef.current) {
        satelliteRef.current = new AMap.TileLayer.Satellite()
        roadNetRef.current = new AMap.TileLayer.RoadNet()
      }
      map.add(satelliteRef.current)
      map.add(roadNetRef.current)
    } else {
      if (satelliteRef.current) {
        map.remove(satelliteRef.current)
        map.remove(roadNetRef.current)
      }
      map.setMapStyle(
        mode === 'clean' ? 'amap://styles/whitesmoke' : 'amap://styles/normal'
      )
    }
  }, [])

  const handleModeChange = useCallback(
    (mode: MapMode) => {
      setMapMode(mode)
      applyMapMode(mode)
    },
    [applyMapMode]
  )

  /** 加载高德 + 拉数据 + 画点 */
  const loadAndRender = useCallback(async () => {
    if (!groupID || (!mapFilter && !queryFilter)) {
      return
    }
    setLoading(true)
    setActiveUserID(null)
    setViewer(null)
    setMemberDrill(null)
    activeUserRef.current = null
    try {
      if (!mapRef.current) {
        const AMap = await AMapLoader.load({
          key: CONFIG.GAODE_KEY,
          version: '2.0',
          plugins: ['AMap.ToolBar']
        })
        aMapRef.current = AMap
        if (!containerRef.current) {
          return
        }
        mapRef.current = new AMap.Map(containerRef.current, {
          zoom: 5,
          zooms: [4, 20],
          mapStyle: 'amap://styles/normal',
          jogEnable: false
        })
        applyMapMode(mapMode)
      }

      // filter:mapFilter 优先(那张卡的范围);否则用当前主页筛选转 v5 同款形状
      const baseFilter = mapFilter
        ? mapFilter
        : buildFetchPhotosParamsWithOptions({
            queryFilter: queryFilter as any,
            groupID,
            isAscSort: false,
            locationRecommend:
              (queryFilter && queryFilter.locationRecommend) || null
          }).filter
      currentFilterRef.current = baseFilter

      if (isBalloonMode) {
        // 排行气球:逐行(每个排行项)按它自己的 mapFilter 各拉一次点,给点打上该项 key
        // (写进 userID 字段,承载名次 key;非真实用户ID),合并后按名次着色。
        // 这样人/天/部门/城市任意维度都通用 —— 后端按行 filter 出照片,前端不用知道每张属于谁。
        const perRow = await Promise.all(
          (rankItems || []).map(async (it) => {
            const r: any = await dispatch(
              fetchPhotoMapAgg({
                groupID,
                filter: it.mapFilter || baseFilter,
                level: 'point',
                pointLimit: PER_ROW_POINT_LIMIT
              })
            )
            const d: PhotoMapAggResult = (r && (r.payload || r)) || EMPTY_RESULT
            return (d.points || []).map((p) => ({ ...p, userID: it.userID }))
          })
        )
        const points = perRow.flat()
        setResult({ ...EMPTY_RESULT, list: [], points })
        setActiveDistrict(null)
        plotBalloons(points)
      } else {
        // 地点聚合:区县→POI
        const res: any = await dispatch(
          fetchPhotoMapAgg({ groupID, filter: baseFilter, level: 'district' })
        )
        const data: PhotoMapAggResult =
          (res && (res.payload || res)) || EMPTY_RESULT
        const list = data.list || []
        const located = list.reduce(
          (sum, d) =>
            sum +
            (d.child || [])
              .filter((c) => c.lat || c.lng)
              .reduce((s, c) => s + (c.photoCnt || 0), 0),
          0
        )
        setResult({ ...data, list, totalWithGeo: located })
        setActiveDistrict(null)
        plotDistricts(list)
      }
    } catch (e) {
      // 接口/网络异常已在 action 内做了 sentry 上报,这里只兜底收尾
    } finally {
      setLoading(false)
    }
  }, [
    dispatch,
    groupID,
    queryFilter,
    mapFilter,
    isBalloonMode,
    rankItems,
    plotDistricts,
    plotBalloons,
    applyMapMode,
    mapMode
  ])

  const destroyMap = useCallback(() => {
    clearMarkers()
    if (mapRef.current) {
      mapRef.current.destroy()
      mapRef.current = null
    }
    satelliteRef.current = null
    roadNetRef.current = null
    setResult(null)
    setActiveDistrict(null)
    setActiveUserID(null)
    setViewer(null)
    setMemberDrill(null)
    activeUserRef.current = null
  }, [clearMarkers])

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => loadAndRender(), 60)
      return () => clearTimeout(timer)
    }
    destroyMap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // 地点聚合模式的右侧排行(总览按区县、下钻按 POI)
  const rankingItems = useMemo(() => {
    if (!result) {
      return []
    }
    const source = activeDistrict
      ? activeDistrict.child || []
      : result.list || []
    return source
      .filter((n) => n.name && n.photoCnt > 0)
      .slice()
      .sort((a, b) => b.photoCnt - a.photoCnt)
  }, [result, activeDistrict])

  const rankingMax = rankingItems.length > 0 ? rankingItems[0].photoCnt : 0

  const handleRankClick = useCallback(
    (node: PhotoMapNode) => {
      if (activeDistrict) {
        // 点地点排行里的 POI:对应 marker 浮顶 + 其余透明 + 放大 + 开该 POI 照片轮播
        const target = poiMarkerByNameRef.current[node.name]
        markersRef.current.forEach((mk) => {
          if (mk.setzIndex) {
            mk.setzIndex(mk === target ? 300 : 100)
          }
        })
        const root = containerRef.current
        if (root) {
          root.querySelectorAll('.ai-photo-map-pin--poi').forEach((n) => {
            const same = n.getAttribute('data-poi') === node.name
            n.classList.toggle('is-active', same)
            n.classList.toggle('is-dim', !same)
          })
        }
        if ((node.lat || node.lng) && mapRef.current) {
          mapRef.current.setZoomAndCenter(16, [node.lng, node.lat])
        }
        openPoiPhotos(node)
      } else if ((node.child || []).length > 0) {
        setActiveDistrict(node)
        plotPois(node)
      }
    },
    [activeDistrict, plotPois, openPoiPhotos]
  )

  // 排行气球模式:每个成员命中的照片张数(用于侧栏)
  const balloonStats = useMemo(() => {
    const cnt: Record<string, number> = {}
    ;(result?.points || []).forEach((p) => {
      if (rankMap.has(p.userID)) {
        cnt[p.userID] = (cnt[p.userID] || 0) + 1
      }
    })
    return cnt
  }, [result, rankMap])

  // 下钻成员后:把 ta 的照片点按坐标聚成"各地点"(封面 + 张数 + 该地点的全部照片)
  const memberClusters = useMemo(() => {
    if (!memberDrill || !result?.points) {
      return []
    }
    const groups = new Map<
      string,
      {
        key: string
        lat: number
        lng: number
        name: string
        photos: PhotoMapPoint[]
      }
    >()
    result.points
      .filter((p) => p.userID === memberDrill.userID && (p.lat || p.lng))
      .forEach((pt) => {
        const place = pt.poi || `${pt.lat.toFixed(6)}|${pt.lng.toFixed(6)}`
        const key = `${memberDrill.userID}|${place}`
        let g = groups.get(key)
        if (!g) {
          g = { key, lat: pt.lat, lng: pt.lng, name: pt.poi || '', photos: [] }
          groups.set(key, g)
        }
        g.photos.push(pt)
      })
    return Array.from(groups.values()).sort(
      (a, b) => b.photos.length - a.photos.length
    )
  }, [memberDrill, result])

  const totalAll = result ? result.totalAll : 0
  const totalWithGeo = result ? result.totalWithGeo : 0
  const balloonShown = result?.points
    ? result.points.filter((p) => rankMap.has(p.userID)).length
    : 0
  const isEmpty =
    !loading &&
    result !== null &&
    (isBalloonMode ? balloonShown === 0 : (result.list || []).length === 0)

  return (
    <Modal
      title="照片地图"
      className="ai-photo-map-modal-root"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width="86%"
      destroyOnHidden
      styles={{ body: { padding: 0, height: '78vh' } }}
    >
      <div className="ai-photo-map-body">
        <div className="ai-photo-map-main">
          <div className="ai-photo-map-toolbar">
            <span className="summary">
              {isBalloonMode ? (
                <>
                  排行榜成员照片 · 地图上 <b>{balloonShown}</b> 张有位置
                </>
              ) : (
                <>
                  {activeDistrict ? `${activeDistrict.name} · ` : ''}共{' '}
                  <b>{totalAll}</b> 张,其中 <b>{totalWithGeo}</b> 张有位置
                </>
              )}
            </span>
            {!isBalloonMode && activeDistrict ? (
              <Button
                size="small"
                className="back-btn"
                onClick={backToDistricts}
              >
                ← 返回区县
              </Button>
            ) : null}
          </div>

          <div className="ai-photo-map-canvas" ref={containerRef} />

          <div className="ai-photo-map-modeswitch">
            {MAP_MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                className={
                  mapMode === m.key
                    ? 'mode-item mode-item--active'
                    : 'mode-item'
                }
                onClick={() => handleModeChange(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="ai-photo-map-loading">
              <Spin tip="加载中…" />
            </div>
          ) : null}

          {isEmpty ? (
            <Empty
              className="ai-photo-map-empty"
              description="当前筛选下没有带位置的照片"
            />
          ) : null}

          {/* 照片轮播浮层:点气球后看该成员的照片,可上一张/下一张,显示第几张 */}
          {viewer ? (
            <div className="ai-photo-viewer">
              <button
                type="button"
                className="v-close"
                onClick={() => setViewer(null)}
              >
                ×
              </button>
              <div
                className="v-img"
                style={{
                  backgroundImage: `url('${
                    viewer.list[viewer.idx]
                      ? viewer.list[viewer.idx].coverUrl
                      : ''
                  }')`
                }}
              />
              <div className="v-bar">
                <button
                  type="button"
                  className="v-nav"
                  disabled={viewer.idx <= 0}
                  onClick={() =>
                    setViewer((v) =>
                      v ? { ...v, idx: Math.max(0, v.idx - 1) } : v
                    )
                  }
                >
                  ‹
                </button>
                <span className="v-meta">
                  {viewer.title} · 第 {viewer.idx + 1}/{viewer.list.length} 张
                </span>
                <button
                  type="button"
                  className="v-nav"
                  disabled={viewer.idx >= viewer.list.length - 1}
                  onClick={() =>
                    setViewer((v) =>
                      v
                        ? { ...v, idx: Math.min(v.list.length - 1, v.idx + 1) }
                        : v
                    )
                  }
                >
                  ›
                </button>
              </div>
              {viewer.list[viewer.idx] && viewer.list[viewer.idx].coverUrl ? (
                <a
                  className="v-link"
                  href={viewer.list[viewer.idx].coverUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  查看原图
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* 右侧面板:排行气球模式 = 成员排行(点名高亮+定位);地点模式 = 地点排行 */}
        <div className="ai-photo-map-side">
          {isBalloonMode ? (
            memberDrill ? (
              <>
                <div className="side-title">
                  <span>
                    <button
                      type="button"
                      className="side-back"
                      onClick={() => setMemberDrill(null)}
                    >
                      ←
                    </button>
                    {memberDrill.name} · 各地点
                  </span>
                  <span className="side-sub">点地点看照片</span>
                </div>
                <div className="side-list">
                  {memberClusters.length === 0 ? (
                    <div className="side-empty">该项暂无带位置的照片</div>
                  ) : (
                    memberClusters.map((c, idx) => {
                      const placeName = c.name || `地点 ${idx + 1}`
                      return (
                        <button
                          key={c.key}
                          type="button"
                          className="rank-row"
                          onClick={() => {
                            if (mapRef.current) {
                              mapRef.current.setZoomAndCenter(16, [
                                c.lng,
                                c.lat
                              ])
                            }
                            selectCluster(c.key)
                            setViewer({
                              list: c.photos,
                              idx: 0,
                              title: `${memberDrill.name} · ${placeName}`
                            })
                          }}
                        >
                          <span className="rank-no">{idx + 1}</span>
                          {c.photos[0] && c.photos[0].coverUrl ? (
                            <span
                              className="rank-cover"
                              style={{
                                backgroundImage: `url('${c.photos[0].coverUrl}')`
                              }}
                            />
                          ) : null}
                          <span className="rank-main">
                            <span className="rank-name" title={placeName}>
                              {placeName}
                            </span>
                          </span>
                          <span className="rank-cnt">{c.photos.length}</span>
                        </button>
                      )
                    })
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="side-title">
                  排行榜
                  <span className="side-sub">点一项看它的各地点</span>
                </div>
                <div className="side-list">
                  {(rankItems || []).map((it) => (
                    <button
                      key={`${it.rank}-${it.userID}`}
                      type="button"
                      className={
                        activeUserID === it.userID
                          ? 'rank-row rank-row--active'
                          : 'rank-row'
                      }
                      onClick={() => {
                        focusMember(it.userID)
                        setMemberDrill({
                          userID: it.userID,
                          rank: it.rank,
                          name: it.name
                        })
                      }}
                    >
                      <span
                        className="rank-dot"
                        style={{
                          background:
                            RANK_COLORS[(it.rank - 1) % RANK_COLORS.length]
                        }}
                      >
                        {it.rank}
                      </span>
                      <span className="rank-main">
                        <span className="rank-name" title={it.name}>
                          {it.name}
                        </span>
                      </span>
                      <span className="rank-cnt">
                        {balloonStats[it.userID] || 0}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )
          ) : (
            <>
              <div className="side-title">
                {activeDistrict
                  ? `${activeDistrict.name} · 地点排行`
                  : '区域排行'}
                <span className="side-sub">按拍照张数</span>
              </div>
              <div className="side-list">
                {rankingItems.length === 0 ? (
                  <div className="side-empty">暂无可排名的地点</div>
                ) : (
                  rankingItems.map((node, idx) => (
                    <button
                      key={`${node.name}-${idx}`}
                      type="button"
                      className="rank-row"
                      onClick={() => handleRankClick(node)}
                    >
                      <span
                        className={idx < 3 ? 'rank-no rank-no--top' : 'rank-no'}
                      >
                        {idx + 1}
                      </span>
                      {node.coverUrl ? (
                        <span
                          className="rank-cover"
                          style={{ backgroundImage: `url('${node.coverUrl}')` }}
                        />
                      ) : null}
                      <span className="rank-main">
                        <span className="rank-name" title={node.name}>
                          {node.name}
                        </span>
                        <span className="rank-bar">
                          <span
                            className="rank-bar-fill"
                            style={{
                              width: `${
                                rankingMax > 0
                                  ? Math.max(
                                      6,
                                      Math.round(
                                        (node.photoCnt / rankingMax) * 100
                                      )
                                    )
                                  : 0
                              }%`
                            }}
                          />
                        </span>
                      </span>
                      <span className="rank-cnt">{node.photoCnt}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PhotoMapDrawer
